import {
  type BuilderError,
  type BuilderField,
  type BuilderFieldKind,
  type BuilderMetadata as FormBuilderMetadata,
  type UseFormBuilderState,
} from '../../../../games/use-form-builder-state.js';
import {
  type BuilderEvent,
  type UsePlaytestBuilderState,
} from '../../../../games/use-playtest-builder-state.js';

// ---------------------------------------------------------------------------
// Per-event adapter that exposes the playtest builder as a
// UseFormBuilderState-shaped object scoped to one event's question list.
//
// FieldsCard / FieldRow / FieldSidePanel from the feedback-form builder
// take `UseFormBuilderState` directly. The playtest builder has N events,
// each with its own question list; this adapter narrows the interface to
// a single event so the same components render the playtest builder's
// per-event modal-style flow without modification.
//
// Form-only metadata fields (channels, routing, repost, recruit, anonymous)
// don't apply here. We stub them with empty values because FieldsCard /
// FieldRow only read `state.fields` and `state.lastError` from the
// builder.state object; the rest of the metadata is unused by those
// components.
// ---------------------------------------------------------------------------

const STUB_FORM_METADATA: FormBuilderMetadata = {
  name: '',
  internalDescription: '',
  title: '',
  discordDescription: '',
  surveyChannelId: null,
  responseChannelId: null,
  repostFrequency: 'never',
  anonymous: false,
  submitResponseTitle: '',
  submitResponseDescription: '',
  recruitChannelIds: [],
  recruitRoleIds: [],
};

// The form builder's BuilderError union includes 'field_cap_reached' and
// the select-options errors; our playtest builder lifts those through
// unchanged. 'event_cap_reached' / 'question_cap_reached' surface at the
// playtest level - the per-event card doesn't need to see those.
function projectError(err: ReturnType<() => BuilderError | 'event_cap_reached' | 'question_cap_reached' | null>): BuilderError | null {
  if (err === null) return null;
  if (err === 'event_cap_reached') return null;
  if (err === 'question_cap_reached') return 'field_cap_reached';
  return err;
}

export function eventFieldsAdapter(
  event: BuilderEvent,
  builder: UsePlaytestBuilderState,
): UseFormBuilderState {
  const eventClientId = event.clientId;
  return {
    state: {
      metadata: STUB_FORM_METADATA,
      fields: event.questions as BuilderField[],
      lastError: projectError(builder.state.lastError),
    },
    reset: () => {
      // Not reachable from the per-event card. Reset is page-level on
      // the form builder; playtest builder owns its own reset path.
    },
    updateMetadata: () => {
      // Not reachable - the per-event card doesn't render metadata
      // (we render event-level inputs separately).
    },
    addField: (kind: BuilderFieldKind, clientId: string) =>
      builder.addQuestion(eventClientId, kind, clientId),
    removeField: (clientId: string) =>
      builder.removeQuestion(eventClientId, clientId),
    reorderFields: (fromIndex: number, toIndex: number) =>
      builder.reorderQuestions(eventClientId, fromIndex, toIndex),
    updateFieldTitle: (clientId: string, value: string) =>
      builder.updateFieldTitle(eventClientId, clientId, value),
    updateFieldDescription: (clientId: string, value: string | null) =>
      builder.updateFieldDescription(eventClientId, clientId, value),
    updateFieldRequired: (clientId: string, value: boolean) =>
      builder.updateFieldRequired(eventClientId, clientId, value),
    setRatingDisplay: (clientId, display) => {
      // RatingDisplay from the form package may include the
      // { mode: 'numbers' } variant; our builder represents "numbers" as
      // the absence of a display key, so map it to undefined here.
      const next = display === undefined || display.mode === 'numbers' ? undefined : display;
      builder.setRatingDisplay(eventClientId, clientId, next);
    },
    setBinaryLabel: (clientId, which, value) =>
      builder.setBinaryLabel(eventClientId, clientId, which, value),
    replaceSelectOption: (clientId, index, value) =>
      builder.replaceSelectOption(eventClientId, clientId, index, value),
    addSelectOption: (clientId) => builder.addSelectOption(eventClientId, clientId),
    removeSelectOption: (clientId, index) =>
      builder.removeSelectOption(eventClientId, clientId, index),
    setSelectMaxValues: (clientId, maxValues) =>
      builder.setSelectMaxValues(eventClientId, clientId, maxValues),
    applyServerIds: () => {
      // Server ids for fields aren't reconciled at the per-event level
      // for the playtest builder - the whole event tree is replaced
      // atomically by replace_playtest_events.
    },
    clearError: () => builder.clearError(),
  };
}
